/**
 * Print the notice template for one notice row of the case report (UX02).
 *
 * The rows are the report's own: the two exchange-notice tables (`victim_path`, `associated`) and the seizure
 * matrix targets. The template is rendered by the existing renderer (`POST /legal/generate-notice`, the same one
 * the Workbench notice dialog calls); nothing here writes template text. The request carries no `case_id`, so
 * printing a template does not add a notice record to the case: the officer completes, prints and serves it.
 *
 * A name the sweep test withheld (`exchange_name` null) never heads the notice: the request names the operator as
 * not yet confirmed.
 */
import { legalService } from '../../services/api';
import { detectChainStrict } from '../../utils/chainDetect';
import { isRec, present, type Rec } from './primitives';
import type { CaseReportPayload } from './types';

export type NoticeRowKind = 'victim_path' | 'associated' | 'target';

/** The `postMessage` type the report document sends to the page that holds it. */
export const NOTICE_TEMPLATE_MESSAGE = 'case-report-notice-template';

/** Name used when the row's exchange name was withheld (vendor lead, stripped sweep): never a vendor's name. */
export const WITHHELD_EXCHANGE_NAME = 'Exchange operator (to be confirmed)';

/** Templates the exchange renderer serves; a matrix row on any other template is a statutory ask with its own form. */
const EXCHANGE_TEMPLATES = new Set(['section91', 'section94']);

export interface NoticeRowRef { kind: NoticeRowKind; index: number }

export interface NoticeCaseInfo { fir_number?: unknown; officer_name?: unknown }

/** The rows of one kind, in the order the report prints them. */
export function noticeRows(d: CaseReportPayload, kind: NoticeRowKind): Rec[] {
  if (kind === 'target') {
    return ((d.seizure_targets?.targets || []) as unknown[]).filter(isRec);
  }
  const en = d.exchange_notices;
  if (!isRec(en)) return [];
  const rows = (en as Rec)[kind];
  return (Array.isArray(rows) ? rows : []).filter(isRec);
}

/** Whether a matrix target prints as a template here (exchange-addressed; the statutory asks are separate forms). */
export function targetHasExchangeTemplate(t: Rec): boolean {
  return EXCHANGE_TEMPLATES.has(String(t.notice_template_key || ''));
}

/** Every printable notice row of a report: `[ref, label]` in report order. */
export function printableNoticeRows(d: CaseReportPayload): { ref: NoticeRowRef; label: string; row: Rec }[] {
  const out: { ref: NoticeRowRef; label: string; row: Rec }[] = [];
  for (const kind of ['victim_path', 'associated'] as const) {
    noticeRows(d, kind).forEach((row, index) => {
      const name = present(row.exchange_name) ? String(row.exchange_name) : 'name withheld';
      const wallet = present(row.exchange_wallet) ? ` ${String(row.exchange_wallet).slice(0, 8)}...` : '';
      out.push({ ref: { kind, index }, label: `${kind === 'victim_path' ? 'Traced funds' : 'Other contact'}: ${name}${wallet}`, row });
    });
  }
  noticeRows(d, 'target').forEach((row, index) => {
    if (!targetHasExchangeTemplate(row)) return;
    const name = present(row.entity_name) ? String(row.entity_name) : 'target';
    out.push({ ref: { kind: 'target', index }, label: `Matrix notice ${index + 1}: ${name}`, row });
  });
  return out;
}

const str = (v: unknown): string => (present(v) ? String(v) : '');

/** NOTICE07: the feeder summaries of a notice block, by id. */
export function feederSummaryById(en: unknown): Map<string, Rec> {
  const list = isRec(en) && Array.isArray((en as Rec).feeder_summaries) ? ((en as Rec).feeder_summaries as unknown[]) : [];
  const out = new Map<string, Rec>();
  for (const s of list) if (isRec(s) && present(s.id)) out.set(String(s.id), s);
  return out;
}

/** NOTICE07: the one-line summary of a feeder (written by the builder; the served notice prints the same line). */
export function feederSummaryLine(s: Rec | undefined): string {
  return s && present(s.line) ? String(s.line) : '';
}

/** The `POST /legal/generate-notice` body for one row. Pure. */
export function noticePayloadForRow(
  kind: NoticeRowKind, row: Rec, info: NoticeCaseInfo, summaries?: Map<string, Rec>,
): Record<string, unknown> {
  const isTarget = kind === 'target';
  const wallet = isTarget
    ? str(row.address)
    : str(row.cited_wallet) || str(row.exchange_wallet) || str(row.deposit_wallet);
  const chain = (isTarget && str(row.chain)) || detectChainStrict(wallet) || '';
  const name = isTarget ? str(row.entity_name) : str(row.exchange_name);
  const withheld = !name || row.name_status === 'stripped_sweep' || row.name_status === 'vendor_lead';
  const group = isTarget ? '' : str(row.feeder_group);
  const summary = group ? summaries?.get(group) : undefined;
  const feederLine = summary && present(summary.line) ? String(summary.line) : '';
  const tx = {
    tx_hash: str(row.tx_hash) || 'N/A',
    amount: typeof row.amount === 'number' ? row.amount : null,
    token_symbol: str(row.token) || null,
    date: str(row.timestamp).slice(0, 10) || 'N/A',
    direction: str(row.direction) || 'N/A',
    counterparty: str(row.outside_wallet) || null,
    counterparty_label: null,
  };
  return {
    exchange_name: withheld ? WITHHELD_EXCHANGE_NAME : name,
    case_context: {
      fir_no: str(info.fir_number),
      fir_date: '',
      sections: '',
      officer_name: str(info.officer_name),
      designation: '',
      police_station: '',
      officer_email: '',
    },
    target_context: {
      wallet_address: wallet,
      chain,
      tx_hash: tx.tx_hash,
      date: tx.date,
      amount: tx.amount ?? 'N/A',
      wallet_role: str(row.wallet_role) || null,
      attribution_method: str(row.name_source) || null,
      transactions: [tx],
      counterparties: [],
      // NOTICE07: a row of a feeder group carries the group's one-line summary; the template prints it above the transactions
      ...(feederLine ? { feeder_summary_line: feederLine } : {}),
    },
    template_key: str(row.template_key) || str(row.notice_template_key) || 'section91',
  };
}

/** Render the template and open it for printing; a blocked pop-up falls back to a download. Resolves with the blob URL. */
export async function openNoticeTemplate(payload: Record<string, unknown>): Promise<string> {
  const { blob } = await legalService.generateNotice(payload);
  const url = window.URL.createObjectURL(blob);
  const tab = window.open(url, '_blank', 'noopener');
  if (!tab) {
    const a = document.createElement('a');
    a.href = url;
    a.download = `Notice_${String(payload.template_key)}_${String(payload.exchange_name)}.pdf`.replace(/\s+/g, '_');
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  return url;
}

/** Look up a row of the report and print its template. Throws on an unknown row or a failed render. */
export async function printNoticeTemplateFor(d: CaseReportPayload, ref: NoticeRowRef): Promise<string> {
  const row = noticeRows(d, ref.kind)[ref.index];
  if (!row) throw new Error('This notice row is no longer in the report.');
  return openNoticeTemplate(noticePayloadForRow(ref.kind, row, (d.case || {}) as NoticeCaseInfo, feederSummaryById(d.exchange_notices)));
}

/** The attribute the report document puts on a print control: `<kind>:<index>`. */
export function refAttr(ref: NoticeRowRef): string {
  return `${ref.kind}:${ref.index}`;
}

/** The inline handler: posts the row to the page that holds the document (the iframe cannot call the API). */
export function printControlHtml(ref: NoticeRowRef): string {
  const msg = `{type:'${NOTICE_TEMPLATE_MESSAGE}',kind:'${ref.kind}',index:${ref.index}}`;
  return `<button type="button" class="screen-only notice-print" data-testid="notice-print-template" data-notice-row="${refAttr(ref)}"`
    + ` onclick="if(window.parent&&window.parent!==window){window.parent.postMessage(${msg},'*')}">Print template</button>`;
}
